<div class="role-management-container">
    <div class="header">
        <h1>Role Management</h1>
        <p class="subtitle">Manage user roles and permissions</p>
        <button class="btn btn-primary" (click)="openCreateModal()" *ngIf="canManageRoles()">
            Create New Role
        </button>
    </div>

    <!-- Success/Error Messages -->
    <div *ngIf="successMessage" class="alert alert-success">
        {{ successMessage }}
    </div>
    <div *ngIf="errorMessage" class="alert alert-error">
        {{ errorMessage }}
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading roles...</p>
    </div>

    <!-- Roles Table -->
    <div *ngIf="!loading" class="table-container">
        <div class="table-header">
            <div class="table-info">
                <span>Total Roles: {{ totalRoles }}</span>
                <span>Page {{ currentPage }} of {{ totalPages }}</span>
            </div>
        </div>

        <table class="roles-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Permissions</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let role of roles" class="role-row">
                    <td>
                        <div class="role-name">{{ role.name }}</div>
                    </td>
                    <td>{{ role.description }}</td>
                    <td>
                        <div class="permissions-list">
                            <span *ngFor="let permission of getPermissionsArray(role.permissions).slice(0, 3)"
                                class="permission-badge">
                                {{ permission }}
                            </span>
                            <span *ngIf="getPermissionsArray(role.permissions).length > 3" class="more-permissions">
                                +{{ getPermissionsArray(role.permissions).length - 3 }} more
                            </span>
                        </div>
                    </td>
                    <td>
                        <span class="type-badge" [class.system]="role.is_system_role">
                            {{ role.is_system_role ? 'System' : 'Custom' }}
                        </span>
                    </td>
                    <td>
                        <span class="status-badge" [class.active]="role.is_active" [class.inactive]="!role.is_active">
                            {{ role.is_active ? 'Active' : 'Inactive' }}
                        </span>
                    </td>
                    <td>{{ formatDate(role.created_at) }}</td>
                    <td>
                        <div class="action-buttons" *ngIf="canManageRoles()">
                            <button class="btn btn-sm btn-primary" (click)="openEditModal(role)"
                                [disabled]="!canModifyRole(role)">
                                Edit
                            </button>
                            <button class="btn btn-sm" [class.btn-success]="!role.is_active"
                                [class.btn-warning]="role.is_active" (click)="openStatusModal(role)"
                                [disabled]="role.is_system_role && !role.is_active">
                                {{ role.is_active ? 'Deactivate' : 'Activate' }}
                            </button>
                            <button class="btn btn-sm btn-danger" (click)="openDeleteModal(role)"
                                [disabled]="!canModifyRole(role)">
                                Delete
                            </button>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination" *ngIf="totalPages > 1">
            <button class="btn btn-sm" [disabled]="currentPage === 1" (click)="onPageChange(currentPage - 1)">
                Previous
            </button>

            <span class="page-info">
                Page {{ currentPage }} of {{ totalPages }}
            </span>

            <button class="btn btn-sm" [disabled]="currentPage === totalPages" (click)="onPageChange(currentPage + 1)">
                Next
            </button>
        </div>
    </div>

    <!-- Create Role Modal -->
    <div *ngIf="showCreateModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal large-modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>Create New Role</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <form (ngSubmit)="createRole()">
                    <div class="form-group">
                        <label for="create_name">Role Name</label>
                        <input type="text" id="create_name" [(ngModel)]="createForm.name" name="name" required>
                    </div>

                    <div class="form-group">
                        <label for="create_description">Description</label>
                        <textarea id="create_description" [(ngModel)]="createForm.description" name="description"
                            rows="3" required></textarea>
                    </div>

                    <div class="form-group">
                        <label>Permissions</label>
                        <div class="permissions-grid">
                            <div *ngFor="let category of getPermissionCategories()" class="permission-category">
                                <h4>{{ category | titlecase }}</h4>
                                <div class="permission-items">
                                    <div *ngFor="let permission of availablePermissions[category]"
                                        class="permission-item">
                                        <label class="permission-checkbox">
                                            <input type="checkbox"
                                                [checked]="isPermissionSelected(category, permission, 'create')"
                                                (change)="togglePermission(category, permission, 'create')">
                                            <span>{{ permission }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                        <button type="submit" class="btn btn-primary" [disabled]="saving">
                            {{ saving ? 'Creating...' : 'Create Role' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <!-- Edit Role Modal -->
    <div *ngIf="showEditModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal large-modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>Edit Role</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <form (ngSubmit)="updateRole()">
                    <div class="form-group">
                        <label for="edit_name">Role Name</label>
                        <input type="text" id="edit_name" [(ngModel)]="editForm.name" name="name" required>
                    </div>

                    <div class="form-group">
                        <label for="edit_description">Description</label>
                        <textarea id="edit_description" [(ngModel)]="editForm.description" name="description" rows="3"
                            required></textarea>
                    </div>

                    <div class="form-group">
                        <label class="permission-checkbox">
                            <input type="checkbox" [(ngModel)]="editForm.is_active" name="is_active">
                            <span>Active</span>
                        </label>
                    </div>

                    <div class="form-group">
                        <label>Permissions</label>
                        <div class="permissions-grid">
                            <div *ngFor="let category of getPermissionCategories()" class="permission-category">
                                <h4>{{ category | titlecase }}</h4>
                                <div class="permission-items">
                                    <div *ngFor="let permission of availablePermissions[category]"
                                        class="permission-item">
                                        <label class="permission-checkbox">
                                            <input type="checkbox"
                                                [checked]="isPermissionSelected(category, permission, 'edit')"
                                                (change)="togglePermission(category, permission, 'edit')">
                                            <span>{{ permission }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                        <button type="submit" class="btn btn-primary" [disabled]="saving">
                            {{ saving ? 'Updating...' : 'Update Role' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <!-- Delete Role Modal -->
    <div *ngIf="showDeleteModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>Delete Role</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <p>Are you sure you want to delete the role <strong>{{ selectedRole?.name }}</strong>?</p>
                <p class="warning-text">This action cannot be undone.</p>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                    <button type="button" class="btn btn-danger" [disabled]="saving" (click)="deleteRole()">
                        {{ saving ? 'Deleting...' : 'Delete Role' }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Toggle Status Modal -->
    <div *ngIf="showStatusModal" class="modal-overlay" (click)="closeModals()">
        <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-header">
                <h3>{{ statusForm.is_active ? 'Deactivate' : 'Activate' }} Role</h3>
                <button class="close-btn" (click)="closeModals()">&times;</button>
            </div>

            <div class="modal-body">
                <p>Are you sure you want to {{ statusForm.is_active ? 'deactivate' : 'activate' }}
                    the role <strong>{{ selectedRole?.name }}</strong>?</p>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" (click)="closeModals()">Cancel</button>
                    <button type="button" class="btn" [class.btn-warning]="statusForm.is_active"
                        [class.btn-success]="!statusForm.is_active" [disabled]="saving" (click)="toggleRoleStatus()">
                        {{ saving ? 'Updating...' : (statusForm.is_active ? 'Deactivate' : 'Activate') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>
